import { useEffect, useState, type Dispatch, type SetStateAction } from "react";

/**
 * ``useState`` whose value survives an F5 within the same tab, backed by
 * ``sessionStorage``.
 *
 * sessionStorage, NOT localStorage, on purpose: the worklist search box can
 * hold a patient name or MRN, and this app keeps PHI out of durable
 * browser storage (see the JWT-in-memory note in ``api/client``).
 * sessionStorage is per-tab and cleared when the tab closes, so a filter —
 * search text included — comes back after a refresh but is gone for the
 * next person who opens a fresh tab on a shared workstation.
 *
 * ``isValid`` guards against a stale or hand-edited stored value: anything
 * that does not pass falls back to ``initial`` rather than feeding a bad
 * value (an unknown DateRange, say) into the query.
 */
export function useSessionState<T>(
  key: string,
  initial: T,
  isValid: (v: unknown) => v is T,
): [T, Dispatch<SetStateAction<T>>] {
  const [value, setValue] = useState<T>(() => {
    try {
      const raw = sessionStorage.getItem(key);
      if (raw !== null) {
        const parsed: unknown = JSON.parse(raw);
        if (isValid(parsed)) return parsed;
      }
    } catch {
      // Storage disabled (private mode) or corrupt JSON — use the default.
    }
    return initial;
  });

  useEffect(() => {
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Quota exceeded or storage disabled — persistence is best-effort.
    }
  }, [key, value]);

  return [value, setValue];
}

/**
 * Drop every persisted value under a key prefix. Called on login so a new
 * session starts from the defaults rather than inheriting the previous
 * user's saved filters on the same tab — sessionStorage is tab-scoped, not
 * session-scoped, so a log-out-then-in without closing the tab would
 * otherwise carry them over.
 */
export function clearSessionByPrefix(prefix: string): void {
  try {
    const keys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i += 1) {
      const k = sessionStorage.key(i);
      if (k && k.startsWith(prefix)) keys.push(k);
    }
    keys.forEach((k) => sessionStorage.removeItem(k));
  } catch {
    // Storage disabled — nothing to clear.
  }
}
